import { useState } from 'react';
import { Mail, Send, ArrowUpRight, Clock, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO = "'IBM Plex Mono', monospace";

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to send message.');
      toast.success(data.message || "Message sent! We'll get back to you within 24 hours.");
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const f = (k) => (e) => setFormData((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-white pt-16 pb-44">
      <SEO
        title="Contact Us — FashionSpyder"
        description="Get in touch with the FashionSpyder team. Questions about our fashion HR intelligence platform? We respond within 24 hours."
        path="/contact"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact FashionSpyder',
          url: 'https://fashionspyder.com/contact',
        }}
      />

      {/* Header band */}
      <section className="px-4 sm:px-6 lg:px-20 border-b border-border">
        <div className="max-w-[1200px] mx-auto py-16 lg:py-20">
          <motion.p
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="text-[hsl(var(--primary))] mb-4" style={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.22em' }}
          >
            ✦ GET IN TOUCH
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground tracking-tight mb-4"
            style={{ fontFamily: SERIF }}
          >
            Let's talk talent.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
            className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed"
          >
            Questions about the platform, your subscription, or a bespoke hiring brief?
            Send us a note and a member of our team will respond within 24 hours.
          </motion.p>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-20 mt-12 lg:mt-16">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">

          {/* ── Contact Form ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
            className="lg:col-span-3"
          >
            <div className="border border-border p-8 lg:p-10 shadow-[6px_6px_0px_hsl(var(--border))]">
              <h2 className="text-2xl font-semibold text-foreground mb-1" style={{ fontFamily: SERIF }}>Send a message</h2>
              <p className="text-sm text-muted-foreground mb-8">We read every message personally.</p>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs uppercase tracking-wide">Your Name</Label>
                    <Input id="name" data-testid="contact-name-input" value={formData.name} onChange={f('name')}
                      placeholder="Jane Smith" className="rounded-none h-11" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs uppercase tracking-wide">Email Address</Label>
                    <Input id="email" type="email" data-testid="contact-email-input" value={formData.email} onChange={f('email')}
                      placeholder="you@company.com" className="rounded-none h-11" required />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-xs uppercase tracking-wide">Company <span className="text-muted-foreground normal-case">(optional)</span></Label>
                  <Input id="company" data-testid="contact-company-input" value={formData.company} onChange={f('company')}
                    placeholder="Your Company Name" className="rounded-none h-11" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-xs uppercase tracking-wide">Message</Label>
                  <Textarea id="message" data-testid="contact-message-input" value={formData.message} onChange={f('message')}
                    placeholder="Tell us how we can help…" className="min-h-[150px] rounded-none resize-none" required />
                </div>

                <Button type="submit" size="lg" disabled={submitting} data-testid="contact-submit-button"
                  className="w-full rounded-none bg-foreground text-background hover:bg-foreground/85 h-12 gap-2">
                  <Send className="w-4 h-4" />
                  {submitting ? 'Sending…' : 'Send Message'}
                </Button>
              </form>
            </div>
          </motion.div>

          {/* ── Sidebar ── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24 }}
            className="lg:col-span-2 space-y-5"
          >
            {/* Email — dark editorial card */}
            <a
              href="mailto:hello@fashionspyder.com"
              data-testid="contact-email-link"
              className="group block bg-[#1e1e1a] p-8 transition-colors hover:bg-[#252520]"
            >
              <div className="w-10 h-10 border border-[hsl(var(--primary)/0.5)] flex items-center justify-center mb-5">
                <Mail className="w-4 h-4 text-[hsl(var(--primary))]" />
              </div>
              <p className="text-white/40 mb-2" style={{ fontFamily: MONO, fontSize: '0.65rem', letterSpacing: '0.2em' }}>
                EMAIL US
              </p>
              <p className="text-lg text-white font-medium flex items-center gap-1.5">
                hello@fashionspyder.com
                <ArrowUpRight className="w-4 h-4 text-[hsl(var(--primary))] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </p>
            </a>

            {/* Response promise */}
            <div className="border border-border p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">24-hour response</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">We reply to every enquiry within one business day.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">For fashion brand teams</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">FashionSpyder is built for in-house hiring teams at fashion houses, studios and agencies.</p>
                </div>
              </div>
            </div>

            {/* Quick links */}
            <div className="border border-border p-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-4" style={{ fontFamily: MONO, letterSpacing: '0.15em' }}>
                Looking for something else?
              </p>
              <ul className="space-y-1">
                {[
                  { to: '/faq', label: 'Frequently asked questions', external: false },
                  { to: '/for-talent', label: 'For fashion professionals', external: false },
                  { to: '/pricing', label: 'Plans & pricing', external: false },
                  { to: 'https://flence.com', label: 'Build your profile on Flence', external: true },
                ].map((item) => (
                  <li key={item.label}>
                    {item.external ? (
                      <a href={item.to} target="_blank" rel="noreferrer"
                        className="group flex items-center justify-between py-2.5 text-sm text-foreground border-b border-border/60 last:border-0 hover:text-[hsl(var(--primary))] transition-colors">
                        {item.label}
                        <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-[hsl(var(--primary))] transition-colors" />
                      </a>
                    ) : (
                      <Link to={item.to}
                        className="group flex items-center justify-between py-2.5 text-sm text-foreground border-b border-border/60 last:border-0 hover:text-[hsl(var(--primary))] transition-colors">
                        {item.label}
                        <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-[hsl(var(--primary))] transition-colors" />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
